import { HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { withStorageSync } from '@angular-architects/ngrx-toolkit';
import { initialStatus, LoadingStatus } from '@bcef/ia-shared';
import { tapResponse } from '@ngrx/operators';
import { patchState, signalStore, withMethods, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { debounceTime, pipe, switchMap, tap } from 'rxjs';

import { ErrorStore } from '@shared/store/error.store';

import { BspceRawForm } from '../interfaces/bspce-raw-form.interface';
import { BspceRequest } from '../interfaces/bspce-request.interface';
import { BspceResponse } from '../interfaces/bspce-response.interface';
import { BspceService } from '../services/bspce.service';

export interface BspceState {
  bspceForm: BspceRawForm;
  bspceRequest: Partial<BspceRequest>;
  bspceResponse: BspceResponse;
  loadingStatus: LoadingStatus;
}

export const initialState: BspceState = {
  bspceForm: {
    acquiredShares: null,
    acquisitionDate: null,
    initialValue: null,
    soldShares: null,
    saleValue: null,
    activityDuration: null,
  },
  bspceRequest: {},
  bspceResponse: { taxAmount: 0, netPV: 0, grossPV: 0 },
  loadingStatus: initialStatus,
};
export const BSPCE_FORM_DEBOUNCE_MILLIS = 300;

export const BspceStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withStorageSync({ key: 'ia-simulateur-epargne:bspce', storage: () => sessionStorage }),
  withMethods(
    (
      store,
      bspceService = inject(BspceService),
      errorStore = inject(ErrorStore),
      router = inject(Router),
    ) => ({
      sendBspceForm: rxMethod<void>(
        pipe(
          tap(() =>
            patchState(store, (state) => ({
              loadingStatus: { ...state.loadingStatus, isLoading: true },
            })),
          ),
          debounceTime(BSPCE_FORM_DEBOUNCE_MILLIS),
          switchMap(() =>
            bspceService.postBspceSimulation(store.bspceRequest()).pipe(
              tapResponse({
                next: (bspceResponse) => {
                  patchState(store, (state) => ({
                    bspceResponse,
                    loadingStatus: { ...state.loadingStatus, isLoaded: true },
                  }));
                  router.navigate(['bspce', 'summary']);
                },
                error: (error: HttpErrorResponse) => {
                  errorStore.setError(error);
                  patchState(store, (state) => ({
                    loadingStatus: { ...state.loadingStatus, error },
                  }));
                  router.navigate(['erreur']);
                },
                finalize: () =>
                  patchState(store, (state) => ({
                    loadingStatus: { ...state.loadingStatus, isLoading: false },
                  })),
              }),
            ),
          ),
        ),
      ),

      addRawFormData(formGroupData: BspceRawForm): void {
        patchState(store, { bspceForm: formGroupData });
      },

      addRequestData(request: Partial<BspceRequest>): void {
        patchState(store, { bspceRequest: request });
      },
    }),
  ),
);

export type IBspceStore = InstanceType<typeof BspceStore>;
